import React from 'react';
import { Divider, IconButton, Link } from '@mui/material';
import MetaTags from '../../../components/MetaTags';
import { usePushEvents } from '../../../hooks/PushEvents';
import { supportShown } from '../../../hooks/PushEvents/clevertapEvents';
import { assetUrl, webConfig } from '../../../utils/webConfig';

const supportLinks = [
    {
        id: 'faq',
        label: 'FAQ',
        href: webConfig.faqURL,
    },
    {
        id: 'contact-us',
        label: 'Contact Us',
        href: webConfig.supportContactUrl,
    },
    {
        id: 'terms-of-service',
        label: 'Terms of Service',
        href: webConfig.termsServiceURL,
    },
];

const socialLinks = [
    {
        id: 'facebook',
        label: 'Facebook',
        href: webConfig.followFacebookURL,
        icon: '/images/svg/facebook-icon.svg',
    },
    {
        id: 'twitter',
        label: 'Twitter',
        href: webConfig.followTwitterURL,
        icon: '/images/svg/twitter-icon.svg',
    },
    {
        id: 'instagram',
        label: 'Instagram',
        href: webConfig.followInstagramURL,
        icon: '/images/svg/instagram-icon.svg',
    },
    {
        id: 'linkedin',
        label: 'LinkedIn',
        href: webConfig.followLinkedinURL,
        icon: '/images/svg/linkedin-icon.svg',
    },
];

export const Support: React.FC = () => {
    const metaTags = React.useMemo(
        () => ({
            title: 'Support | Givelify',
            description: 'Givelify support',
        }),
        [],
    );
    const { pushEvent } = usePushEvents();
    React.useEffect(
        () => {
            pushEvent(supportShown);
        },
        //run only once when component is mounted
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return (
        <React.Fragment>
            <MetaTags {...metaTags} />
            <div
                id="support-page"
                data-testid="support"
                className="rounded-[32px] shadow-high bg-white w-full sm:ml-8 sm:mr-0 mx-4 overflow-hidden max-w-[792px]"
            >
                <div
                    id="support-body"
                    data-testid="wallet-body"
                    className="w-full flex flex-col items-center p-6 py-8 sm:pb-16 sm:pt-16"
                >
                    <div
                        id="support-title"
                        data-testid="wallet-title"
                        className="text-heading-large text-primary mb-8"
                    >
                        Support
                    </div>
                    <div
                        id="support-links"
                        className="w-full max-w-[360px]"
                    >
                        {supportLinks.map((link, index) => (
                            <React.Fragment key={link.id}>
                                {index === 0 && (
                                    <Divider id="support-divider-top" />
                                )}
                                <Link
                                    id={`support-link-${link.id}`}
                                    href={link.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="none"
                                    className="flex w-full items-center justify-between py-[15px] text-secondary"
                                >
                                    <span
                                        id={`support-link-label-${link.id}`}
                                        className="text-body-medium"
                                    >
                                        {link.label}
                                    </span>
                                    <img
                                        id={`support-link-chevron-${link.id}`}
                                        src={assetUrl(
                                            '/images/svg/previous_arrow.svg',
                                        )}
                                        alt=""
                                        className="h-[13px] w-2"
                                    />
                                </Link>
                                <Divider
                                    id={`support-divider-after-${link.id}`}
                                />
                            </React.Fragment>
                        ))}
                    </div>
                    <div
                        id="support-follow-us"
                        className="mt-12 flex flex-col items-center"
                    >
                        <div
                            id="support-follow-us-label"
                            className="text-body-medium-bold text-secondary"
                        >
                            Follow Us
                        </div>
                        <div
                            id="support-social-links"
                            className="mt-3 flex items-center justify-center gap-2"
                        >
                            {socialLinks.map((link) => (
                                <IconButton
                                    id={`support-social-${link.id}`}
                                    key={link.id}
                                    component="a"
                                    href={link.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`Follow us on ${link.label}`}
                                    disableRipple
                                    className="h-12 w-12 bg-[#3a4c63] p-0 hover:bg-secondary"
                                >
                                    <img
                                        id={`support-social-icon-${link.id}`}
                                        src={assetUrl(link.icon)}
                                        alt=""
                                        className="h-12 w-12"
                                    />
                                </IconButton>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};
